import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, StickyNote } from "lucide-react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { MoveFilters, rangeToSince } from "@/components/MoveFilters";
import { api } from "@/api/client";
import { colors, spacing, fonts } from "@/theme";

const FILTERS = ["all", "pending", "acknowledged"];

function fmt(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function Board() {
  const [filter, setFilter] = useState("all");
  const [yardFilter, setYardFilter] = useState("all");
  const [yards, setYards] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");

  const load = useCallback(async () => {
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const data = await api(`/moves${q}`);
      setMoves(data);
    } catch (e) { console.error(e); }
  }, [filter]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/locations");
        setYards(r.yards || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const remove = async (id) => {
    try { await api(`/moves/${id}`, { method: "DELETE" }); load(); } catch (e) { console.error(e); }
  };

  const filteredMoves = useMemo(() => {
    const since = rangeToSince(range);
    const q = search.trim().toLowerCase();
    return moves.filter((m) => {
      if (yardFilter !== "all" && m.yard !== yardFilter) return false;
      if (since && new Date(m.created_at) < since) return false;
      if (q) {
        const hay = `${m.trailer_number} ${m.from_location} ${m.to_location} ${m.created_by_name} ${m.acknowledged_by_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [moves, yardFilter, range, search]);

  const pendingCount = filteredMoves.filter((m) => m.status === "pending").length;

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader
        title="Move Board"
        subtitle={`${filteredMoves.length} total · ${pendingCount} pending${yardFilter !== "all" ? ` · ${yardFilter}` : ""}`}
      />
      <div style={s.controls}>
        <div style={s.chipsRow}>
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button key={f} data-testid={`chip-${f}`} onClick={() => setFilter(f)}
                style={{ ...s.chip, ...(active ? s.chipActive : {}) }}>
                <span style={{ ...s.chipText, color: active ? colors.onSurfaceInverse : colors.onSurface }}>{f}</span>
              </button>
            );
          })}
        </div>
        <div style={s.yardChipsRow}>
          <YardChip label="All yards" count={moves.length} active={yardFilter === "all"} onClick={() => setYardFilter("all")} testId="yard-chip-all" />
          {yards.map((y) => (
            <YardChip key={y} label={y} count={moves.filter((m) => m.yard === y).length} active={yardFilter === y} onClick={() => setYardFilter(y)} testId={`yard-chip-${y}`} />
          ))}
        </div>
      </div>
      <MoveFilters search={search} onSearchChange={setSearch} range={range} onRangeChange={setRange} placeholder="Search trailer, location, or driver" />

      {loading ? (
        <div style={s.spinnerWrap}><div className="yo-spinner" /></div>
      ) : filteredMoves.length === 0 ? (
        <div style={s.empty}>{moves.length === 0 ? "No moves yet. Create one." : "No moves match your filters."}</div>
      ) : (
        <div style={s.grid}>
          {filteredMoves.map((item) => (
            <div key={item.id} data-testid={`move-${item.id}`} style={{ ...s.card, ...(item.status === "acknowledged" ? s.cardDone : {}) }}>
              <div style={s.cardTop}>
                <div>
                  <div style={s.trailer}>{item.trailer_number}</div>
                  {item.yard ? <div style={s.yardTag}>{item.yard}</div> : null}
                </div>
                <div style={{ ...s.badge, ...(item.status === "acknowledged" ? s.badgeDone : s.badgePending) }}>
                  <span style={s.badgeText}>{item.status}</span>
                </div>
              </div>
              <div style={s.route}>
                <span style={s.loc}>{item.from_location}</span>
                <ArrowRight size={18} color={colors.onSurfaceTertiary} />
                <span style={s.loc}>{item.to_location}</span>
              </div>
              <div style={s.meta}>Created · {item.created_by_name} · {fmt(item.created_at)}</div>
              {item.acknowledged_at && (
                <div style={{ ...s.meta, color: colors.success }}>Ack · {item.acknowledged_by_name} · {fmt(item.acknowledged_at)}</div>
              )}
              {item.notes && item.notes.trim().length > 0 ? (
                <div style={s.notesBox}>
                  <StickyNote size={14} color={colors.warning} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={s.notesText}>{item.notes.trim()}</span>
                </div>
              ) : null}
              {item.status === "pending" && (
                <button data-testid={`delete-${item.id}`} style={s.deleteBtn} onClick={() => remove(item.id)}>
                  <span style={s.deleteText}>Cancel move</span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function YardChip({ label, count, active, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} style={{ ...s.yardChip, ...(active ? s.yardChipActive : {}) }}>
      <span style={{ ...s.yardChipText, color: active ? colors.onBrandPrimary : colors.onSurfaceTertiary }}>{label}</span>
      <span style={{ ...s.yardBadge, ...(active ? s.yardBadgeActive : {}) }}>
        <span style={{ ...s.yardBadgeText, color: active ? colors.brandPrimary : colors.onSurfaceTertiary }}>{count}</span>
      </span>
    </button>
  );
}

const s = {
  controls: { padding: `${spacing.lg}px ${spacing.xl}px 0` },
  chipsRow: { display: "flex", gap: spacing.sm },
  chip: { padding: `10px ${spacing.md}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, cursor: "pointer", textTransform: "capitalize" },
  chipActive: { backgroundColor: colors.onSurface },
  chipText: { ...fonts.monoBold, fontSize: 11, letterSpacing: 0 },
  yardChipsRow: { display: "flex", flexWrap: "wrap", gap: spacing.sm, paddingTop: spacing.sm },
  yardChip: { display: "flex", alignItems: "center", gap: spacing.xs, padding: `8px ${spacing.md}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  yardChipActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  yardChipText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  yardBadge: { minWidth: 22, padding: "1px 6px", borderWidth: 1, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceTertiary, textAlign: "center" },
  yardBadgeActive: { backgroundColor: colors.onBrandPrimary, borderColor: colors.onBrandPrimary },
  yardBadgeText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  spinnerWrap: { display: "flex", justifyContent: "center", marginTop: 40 },
  empty: { textAlign: "center", marginTop: 48, ...fonts.monoBold, color: colors.onSurfaceTertiary },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: spacing.md, padding: spacing.xl },
  card: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.lg },
  cardDone: { borderColor: colors.borderStrong, backgroundColor: colors.surfaceTertiary },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing.sm },
  trailer: { ...fonts.monoBold, fontSize: 22, color: colors.onSurface },
  yardTag: { ...fonts.monoBold, fontSize: 11, color: colors.brandPrimary, marginTop: 2 },
  badge: { padding: `4px ${spacing.sm}px`, borderWidth: 2, borderStyle: "solid" },
  badgePending: { borderColor: colors.warning, backgroundColor: colors.onWarning },
  badgeDone: { borderColor: colors.success, backgroundColor: colors.onSuccess },
  badgeText: { ...fonts.monoBold, fontSize: 10, color: colors.onSurface, textTransform: "capitalize" },
  route: { display: "flex", alignItems: "center", gap: spacing.sm, margin: `${spacing.sm}px 0`, flexWrap: "wrap" },
  loc: { ...fonts.monoBold, fontSize: 14, color: colors.onSurface },
  meta: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: 4 },
  notesBox: { display: "flex", gap: spacing.xs, alignItems: "flex-start", borderLeft: `3px solid ${colors.warning}`, backgroundColor: colors.onWarning, padding: `6px ${spacing.sm}px`, marginTop: spacing.sm },
  notesText: { flex: 1, ...fonts.mono, fontSize: 12, color: colors.onSurface, lineHeight: 1.35 },
  deleteBtn: { display: "block", width: "100%", marginTop: spacing.md, borderWidth: 2, borderStyle: "solid", borderColor: colors.error, padding: spacing.sm, backgroundColor: "transparent", cursor: "pointer" },
  deleteText: { ...fonts.monoBold, fontSize: 12, color: colors.error },
};
